'use client';

import React from 'react';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGraduationCap,
  faCalendarCheck,
  faFileSignature,
  faPiggyBank,
  faSearchDollar,
  faBuildingColumns,
  faHandHoldingHeart,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import Table from '@/components/Table/Table';
import { Column } from '@/components/Table/Table.types';
import styles from './Timeline.module.css';


const TIMELINE_STAGES = [
  {
    id: 'apps',
    title: 'Application Launch & Essay Finalization',
    period: 'August – September 2026',
    icon: faGraduationCap,
    items: [
      'Common App and Coalition App officially open August 1',
      'Senior Action Items: Draft and polish primary personal statements',
      'Request formal recommendation letters from core 11th-grade teachers and guidance counselors',
      'Foundation Support: Attend our September Roadmap to Launch workshop',
    ],
  },
  {
    id: 'fafsa',
    title: 'FAFSA Launch, CSS Profile & Early Prep',
    period: 'October 2026',
    icon: faFileSignature,
    items: [
      'The 2027–2028 FAFSA and CSS Profile open on October 1',
      'Create FSA IDs early',
      'Testing Window: Juniors take the PSAT/NMSQT for National Merit Scholarship qualification',
      'Early Prep: Finalize supplemental essays for upcoming early application deadlines',
    ],
  },
  {
    id: 'early',
    title: 'Early Application & Priority Scholarship Deadlines',
    period: 'November 1–15, 2026',
    icon: faFileSignature,
    items: [
      'Early Action (EA) and binding Early Decision I (ED I) deadlines hit for most major universities',
      'National scholarship deadlines (e.g., Jack Kent Cooke, Gates Scholarship) peak during early November',
    ],
  },
  {
    id: 'decision',
    title: 'Early Decision Notifications & Winter Push',
    period: 'December 2026',
    icon: faBuildingColumns,
    items: [
      'Early Decision Results: Students receiving ED I acceptances must confirm enrollment and withdraw other applications',
      'Regular Decision Polish: Finalize essays and forms for Regular Decision (RD) deadlines (typically Jan 1–15)',
    ],
  },
  {
    id: 'regular',
    title: 'Regular Decision & ED II Deadlines',
    period: 'January 1–15, 2027',
    icon: faCalendarCheck,
    items: [
      'Bulk Regular Decision and Early Decision II (ED II) applications are due',
      'Mid-Year Transcripts: Verify school counselors submit first-semester senior grades to college portals',
    ],
  },
  {
    id: 'state',
    title: 'State Aid Deadlines & Local Scholarships',
    period: 'February – March 2027',
    icon: faPiggyBank,
    items: [
      'FAFSA Priority: State-level priority deadlines (e.g., Georgia HOPE/Zell Miller requirements)',
      'Local Funding: Peak application window for community, non-profit, and regional scholarships',
    ],
  },
  {
    id: 'admissions',
    title: 'Admissions Decisions & Financial Aid Audits',
    period: 'Late March – April 2027',
    icon: faBuildingColumns,
    items: [
      'Regular Decision admissions results and financial award letters arrive',
      'Financial Gap Review: Compare award packages, calculate true out-of-pocket costs, and submit financial aid appeals if needed',
    ],
  },
  {
    id: 'decision-day',
    title: 'National Decision Day & AP/IB Exams',
    period: 'May 1–15, 2027',
    icon: faHandHoldingHeart,
    items: [
      'May 1 (National Decision Day): Final deadline to submit enrollment deposits to your selected college',
      'Advanced Placement & IB: AP and IB exams take place nationwide during the first two weeks of May',
    ],
  },
];

const columns: Column<any>[] = [
  {
    key: 'period',
    title: 'Timeline',
    render: (row: any) => (
      <td className={styles.cell}>
        <FontAwesomeIcon icon={faCalendarCheck} className={styles.cellIcon} />
        {row.period}
      </td>
    ),
  },
  {
    key: 'title',
    title: 'Milestone',
    render: (row: any) => (
      <td className={styles.cell}>
        <FontAwesomeIcon icon={row.icon} className={styles.cellIcon} />
        <strong>{row.title}</strong>
      </td>
    ),
  },
  {
    key: 'count',
    title: 'Action Items',
    render: (row: any) => (
      <td className={styles.cell}>{row.items.length} items</td>
    ),
  },
];

export default function TimelinePage() {
  return (
    <div className={styles.container}>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <h1>Master Academic &amp; Financial Aid Timeline</h1>
          <p>
            Your complete month-by-month roadmap for the 2026–2027 fiscal year.
            From application launch to National Decision Day — we have you covered.
          </p>
          <Link href="/sessions" className={styles.ctaButton}>
            View Sessions <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </section>

      {/* Introduction */}
      <section className={styles.intro}>
        <div className={styles.containerNarrow}>
          <h2>Save This Issue!</h2>
          <p>
            Below is your month-by-month master timeline and strategic roadmap for
            the upcoming fiscal year. Whether your student is a Freshman building
            their high school foundation or a Senior finalizing college
            applications, timing is everything.
          </p>
        </div>
      </section>

      {/* Timeline Stages */}
      <section className={styles.section}>
        <div className={styles.containerNarrow}>
          <Table
            data={TIMELINE_STAGES}
            columns={columns}
            keyField="id"
            emptyState="No timeline data available"
            bordered={true}
            hoverable={true}
          />
        </div>
      </section>

      {/* Detailed Breakdown */}
      <section className={styles.detailSection}>
        <div className={styles.containerNarrow}>
          <h2 className={styles.sectionTitle}>Detailed Breakdown</h2>
          <p className={styles.sectionDescription}>
            Each milestone contains specific action items tailored to your student&apos;s
            grade level and preparation needs.
          </p>
          <div className={styles.stageList}>
            {TIMELINE_STAGES.map((stage, idx) => (
              <details key={stage.id} className={styles.stageCard}>
                <summary className={styles.stageHeader}>
                  <span className={styles.stageNumber}>
                    {idx + 1}
                  </span>
                  <div className={styles.stageInfo}>
                    <FontAwesomeIcon icon={stage.icon} className={styles.stageIcon} />
                    <div>
                      <h3 className={styles.stageTitle}>{stage.title}</h3>
                      <span className={styles.stagePeriod}>{stage.period}</span>
                    </div>
                  </div>
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className={styles.stageChevron}
                  />
                </summary>
                <ul className={styles.stageItems}>
                  {stage.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Reference Table */}
      <section className={styles.quickRef}>
        <div className={styles.containerNarrow}>
          <h2>Quick Reference</h2>
          <div className={styles.quickGrid}>
            <div className={styles.quickCard}>
              <FontAwesomeIcon icon={faGraduationCap} size="2x" color="#4a90e2" />
              <h3>Applications Open</h3>
              <p>August 1 — Common App and Coalition App</p>
            </div>
            <div className={styles.quickCard}>
              <FontAwesomeIcon icon={faFileSignature} size="2x" color="#f5a623" />
              <h3>FAFSA &amp; CSS</h3>
              <p>October 1 — 2027–2028 financial aid forms open</p>
            </div>
            <div className={styles.quickCard}>
              <FontAwesomeIcon icon={faCalendarCheck} size="2x" color="#50e3c2" />
              <h3>Early Deadlines</h3>
              <p>November 1–15 — EA and ED I</p>
            </div>
            <div className={styles.quickCard}>
              <FontAwesomeIcon icon={faBuildingColumns} size="2x" color="#e24a4a" />
              <h3>Regular Decision</h3>
              <p>January 1–15 — Bulk RD and ED II</p>
            </div>
            <div className={styles.quickCard}>
              <FontAwesomeIcon icon={faSearchDollar} size="2x" color="#9b59b6" />
              <h3>Financial Aid Audits</h3>
              <p>March–April — Compare packages and appeal</p>
            </div>
            <div className={styles.quickCard}>
              <FontAwesomeIcon icon={faHandHoldingHeart} size="2x" color="#1abc9c" />
              <h3>National Decision Day</h3>
              <p>May 1 — Submit enrollment deposits</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
